import { useEffect, useState } from 'react';
import { Modal } from 'us-react-components/dist/Modal/Modal';
import {
  namespaces,
  useSharedTranslation,
} from 'us-react-components/dist/hooks/useSharedTranslation';
import { Button, ButtonType } from '../../../../components/Button';
import { ModalBase } from './types';

// -- Location Edit Modal ---------------------------------------------------
export const LocationEditModal = ({
  isOpen,
  onClose,
  initialAddress,
  initialWorksWithin,
  onSave,
}: ModalBase & {
  initialAddress?: {
    street?: string;
    street2?: string;
    city?: string;
    state?: string;
    zip?: string;
    neighborhood?: string;
  };
  initialWorksWithin?: number;
  onSave: (patch: {
    address: {
      street?: string;
      street2?: string;
      city: string;
      state: string;
      zip: string;
      neighborhood?: string;
    };
    worksWithin?: number;
  }) => Promise<void>;
}): JSX.Element => {
  const { t } = useSharedTranslation(namespaces.caregiver);
  const [street, setStreet] = useState(initialAddress?.street ?? '');
  const [street2, setStreet2] = useState(initialAddress?.street2 ?? '');
  const [city, setCity] = useState(initialAddress?.city ?? '');
  const [state, setState] = useState(initialAddress?.state ?? '');
  const [zip, setZip] = useState(initialAddress?.zip ?? '');
  const [neighborhood, setNeighborhood] = useState(initialAddress?.neighborhood ?? '');
  const [worksWithin, setWorksWithin] = useState(String(initialWorksWithin ?? ''));
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (isOpen) {
      setStreet(initialAddress?.street ?? '');
      setStreet2(initialAddress?.street2 ?? '');
      setCity(initialAddress?.city ?? '');
      setState(initialAddress?.state ?? '');
      setZip(initialAddress?.zip ?? '');
      setNeighborhood(initialAddress?.neighborhood ?? '');
      setWorksWithin(String(initialWorksWithin ?? ''));
      setError('');
    }
  }, [isOpen, initialAddress, initialWorksWithin]);

  const submit = async (): Promise<void> => {
    if (!city.trim() || !state.trim() || !zip.trim()) {
      setError('City, state, and ZIP are required.');
      return;
    }
    setSubmitting(true);
    try {
      // Send the full address with edits overlaid so lat/lng and server-required fields survive the PATCH.
      await onSave({
        address: {
          ...(initialAddress ?? {}),
          street: street || undefined,
          street2: street2 || undefined,
          city: city.trim(),
          state: state.trim().toUpperCase(),
          zip: zip.trim(),
          neighborhood: neighborhood || undefined,
        },
        worksWithin: worksWithin ? Number(worksWithin) : undefined,
      });
      onClose();
    } catch {
      setError("Couldn't save. Try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={t('pages.sitterProfile.modals.editLocation')}
      className='w-full max-w-xl'
      footer={
        <>
          <Button buttonType={ButtonType.tertiary} onClick={onClose}>
            Cancel
          </Button>
          <Button
            buttonType={ButtonType.primary}
            disabled={submitting}
            onClick={submit}
          >
            Save
          </Button>
        </>
      }
    >
      <p className='mb-1 font-semibold text-base'>Add your street address:</p>
      <p className='text-sm mb-4 text-neutral-600'>
        To calculate distance. Your address will NOT be public.
      </p>
      <div className='grid grid-cols-12 gap-3 mb-3'>
        <div className='col-span-7'>
          <label htmlFor='sitter-street' className='block text-base font-semibold mb-1'>
            Street
          </label>
          <input
            id='sitter-street'
            type='text'
            className='form-control w-full border border-neutral-300 rounded px-3 py-2'
            placeholder={t('pages.sitterProfile.modals.placeholderStreet')}
            value={street}
            onChange={(e): void => setStreet(e.target.value)}
          />
        </div>
        <div className='col-span-5'>
          <label htmlFor='sitter-apt' className='block text-base font-semibold mb-1'>
            Apt #
          </label>
          <input
            id='sitter-apt'
            type='text'
            className='form-control w-full border border-neutral-300 rounded px-3 py-2'
            placeholder={t('pages.sitterProfile.modals.placeholderApt')}
            value={street2}
            onChange={(e): void => setStreet2(e.target.value)}
          />
        </div>
      </div>
      <div className='grid grid-cols-12 gap-3 mb-3'>
        <div className='col-span-7'>
          <label htmlFor='sitter-city' className='block text-base font-semibold mb-1'>
            City
          </label>
          <input
            id='sitter-city'
            type='text'
            className='form-control w-full border border-neutral-300 rounded px-3 py-2'
            placeholder={t('pages.sitterProfile.modals.placeholderCity')}
            value={city}
            onChange={(e): void => setCity(e.target.value)}
          />
        </div>
        <div className='col-span-5'>
          <label htmlFor='sitter-state' className='block text-base font-semibold mb-1'>
            State
          </label>
          <input
            id='sitter-state'
            type='text'
            className='form-control w-full border border-neutral-300 rounded px-3 py-2'
            placeholder={t('pages.sitterProfile.modals.placeholderState')}
            maxLength={2}
            value={state}
            onChange={(e): void => setState(e.target.value)}
          />
        </div>
      </div>
      <div className='grid grid-cols-12 gap-3 mb-3'>
        <div className='col-span-4'>
          <label htmlFor='sitter-zip' className='block text-base font-semibold mb-1'>
            Zip Code
          </label>
          <input
            id='sitter-zip'
            type='text'
            className='form-control w-full border border-neutral-300 rounded px-3 py-2'
            placeholder={t('pages.sitterProfile.modals.placeholderZip')}
            maxLength={5}
            value={zip}
            onChange={(e): void => setZip(e.target.value)}
          />
        </div>
        <div className='col-span-8'>
          <label
            htmlFor='sitter-neighborhood'
            className='block text-base font-semibold mb-1'
          >
            Neighborhood
          </label>
          <input
            id='sitter-neighborhood'
            type='text'
            className='form-control w-full border border-neutral-300 rounded px-3 py-2'
            placeholder={t('pages.sitterProfile.modals.placeholderNeighborhood')}
            value={neighborhood}
            onChange={(e): void => setNeighborhood(e.target.value)}
          />
        </div>
      </div>
      <label htmlFor='works-within-distance' className='block font-semibold mb-1 mt-5 text-base'>
        Willing to work within:
      </label>
      <select
        id='works-within-distance'
        className='form-control w-full border border-neutral-300 rounded px-3 py-2 mb-3'
        value={worksWithin}
        onChange={(e): void => setWorksWithin(e.target.value)}
      >
        <option value=''>Any distance</option>
        <option value='3'>3 miles</option>
        <option value='5'>5 miles</option>
        <option value='10'>10 miles</option>
        <option value='15'>15 miles</option>
        <option value='25'>25 miles</option>
        <option value='50'>50 miles</option>
      </select>
      {error && <p className='text-red-600 text-sm mb-2'>{error}</p>}
    </Modal>
  );
};
